// src/Pages/Dashboard.jsx
import React, { useEffect,useState } from "react";
import Navigation from "../Components/Navigation";
import API from "../api/config.js";

// ---------------- UI atoms ----------------
const IconWrap = ({ children, tone = "orange" }) => {
  const toneMap = {
    orange: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300",
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300",
    rose: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300",
  };
  return (
    <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${toneMap[tone]}`}>
      {children}
    </div>
  );
};

const HexIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
    <path d="M12 1.8 21 6.9v10.2L12 22.2 3 17.1V6.9L12 1.8z" />
  </svg>
);
const QrIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
    <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zM13 3h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zM17 13h-4v2h2v2h2v-4zm-4 6h2v2h-2v-2zm8-6h-2v2h2v-2zm-4 4h4v4h-4v-4z" />
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
    <path d="M16 11a4 4 0 1 0-3.999-4A4 4 0 0 0 16 11zm-8 2a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-3.314 0-6 1.79-6 4v2h8v-2c0-1.021.401-1.954 1.072-2.727A8.64 8.64 0 0 0 8 15zm8 0c-1.7 0-3.23.43-4.43 1.159A5.01 5.01 0 0 0 11 19v2h11v-2c0-2.21-2.686-4-6-4z" />
  </svg>
);

const StatCard = ({ icon, tone, value, label, delta }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center gap-4">
      <IconWrap tone={tone}>
        {icon === "hex" && <HexIcon />}
        {icon === "qr" && <QrIcon />}
        {icon === "users" && <UsersIcon />}
      </IconWrap>
      <div>
        <div className="text-4xl font-semibold text-gray-900 dark:text-gray-100">
          {Number(value || 0).toLocaleString()}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
        <div className="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          {delta}
        </div>
      </div>
    </div>
  </div>
);

// Pills & small UI bits (reusing Users.jsx vibe)
const StatusPill = ({ status }) => {
  const s = (status || "").toUpperCase();
  const map =
    s === "ACTIVE"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
      : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${map}`}>{s || "INACTIVE"}</span>;
};


const initialsOf = (full = "") =>
  full.split(" ").filter(Boolean).map((p) => p[0]?.toUpperCase()).slice(0, 2).join("");

// ---------------- Page ----------------
const Dashboard = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem("travelxoxo-theme") || "light");
  const [activeScreen, setActiveScreen] = useState("dashboard");

  // Stats: Total Items, QR Scans, Active Users
  const [stats, setStats] = useState([
    { id: "total", label: "Total Items", value: 0, delta: "vs last month", icon: "hex" },
    { id: "scans", label: "QR Scans", value: 0, delta: "vs last month", icon: "qr" },
    { id: "active", label: "Active Users", value: 0, delta: "vs last month", icon: "users" },
  ]);

  // Recently registered users (last 10)
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // theme
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("travelxoxo-theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  // fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [qrcodesRes, registrationsRes] = await Promise.all([
        API.get("/qrcodes"),
        API.get("/registrations"),
      ]);

      const qrcodes = Array.isArray(qrcodesRes?.data) ? qrcodesRes.data : [];
      // If your /registrations returns { registrations: [...] }, support both shapes:
      const regData = Array.isArray(registrationsRes?.data)
        ? registrationsRes.data
        : registrationsRes?.data?.registrations || [];

      // metrics
      const totalItems = qrcodes.length;
      const totalScans = qrcodes.reduce((sum, q) => sum + Number(q.scan_count || 0), 0);
      const activeUsers = new Set(regData.map((r) => r.owner || r.owner_name || r.user_name).filter(Boolean)).size;

      setStats([
        { id: "total", label: "Total Items", value: totalItems, delta: "vs last month", icon: "hex" },
        { id: "scans", label: "QR Scans", value: totalScans, delta: "vs last month", icon: "qr" },
        { id: "active", label: "Active Users", value: activeUsers, delta: "vs last month", icon: "users" },
      ]);

      // last 10 registered users
      const formatted = regData
        .map((u) => ({
          id: u.user_id ?? u.id ?? u._id ?? `${u.owner || u.owner_name || u.user_name}-${u.qr_id || ""}`,
          name: u.owner ?? u.owner_name ?? u.user_name ?? "—",
          email: u.email ?? "—",
          phone: u.primary_phone ?? u.phone ?? u.alt_phone ?? "—",
          organization: u.organization ?? "—",
          items: 1, // each registration row = 1 item registered; adjust if you aggregate per user
          joined: u.createdAt ?? u.date_registered ?? u.join_date ?? u.date ?? null,
          status: ((u.status || "").toUpperCase() === "ACTIVE") ? "ACTIVE" : "INACTIVE",
        }))
        .sort((a, b) => new Date(b.joined || 0) - new Date(a.joined || 0))
        .slice(0, 10);

      setRecentUsers(formatted);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt)) return "—";
    // Match Users.jsx display
    return dt.toLocaleDateString("en-GB", { month: "short", day: "2-digit", year: "numeric" });
  };

  return (
    <div className="flex bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <Navigation
        activeScreen={activeScreen}
        setActiveScreen={setActiveScreen}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <main className="flex-1 ml-20 md:ml-64 p-6 md:p-10">
        {/* Stat cards (3 only) */}
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          <StatCard icon="hex"   tone="orange" value={stats[0].value} label={stats[0].label} delta={stats[0].delta} />
          <StatCard icon="qr"    tone="blue"   value={stats[1].value} label={stats[1].label} delta={stats[1].delta} />
          <StatCard icon="users" tone="rose"   value={stats[2].value} label={stats[2].label} delta={stats[2].delta} />
        </div>

        {/* Recently Registered Users (last 10) */}
        <div className="mt-8 rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between px-6 pt-5">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recently Registered Users</h2>
            <button
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={() => window.location.assign("/users")}
            >
              View All
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                <tr>
                  <th className="px-6 py-4 font-semibold">User</th>
                  <th className="px-6 py-4 font-semibold">Email</th>
                  <th className="px-6 py-4 font-semibold">Phone</th>
                  <th className="px-6 py-4 font-semibold">Organization</th>
                  <th className="px-6 py-4 font-semibold">Items</th>
                  <th className="px-6 py-4 font-semibold">Join Date</th>
                  <th className="px-6 py-4 font-semibold">Status</th>
                  
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : recentUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  recentUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            <span className="text-sm font-bold">{initialsOf(u.name)}</span>
                          </div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{u.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700 dark:text-gray-200">{u.email}</td>
                      <td className="px-6 py-4 text-gray-700 dark:text-gray-200">{u.phone}</td>
                      <td className="px-6 py-4 text-gray-700 dark:text-gray-200">{u.organization}</td>
                      <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-100">{u.items}</td>
                      <td className="px-6 py-4 text-gray-700 dark:text-gray-200">{fmtDate(u.joined)}</td>
                      <td className="px-6 py-4"><StatusPill status={u.status} /></td>
                      
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
